import { useMutation, useQuery } from "@tanstack/react-query";
import PurchaseOrderService from "../services/PurchaseOrder/PurchaseOrderService";
import { useEffect, useState } from "react";
import { Button, Flex, message, Popconfirm, Tooltip, Grid } from "antd";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/pro-solid-svg-icons";
import { queryClient } from "../react-query-client";
import { useNavigate } from "react-router-dom";
import { PURCHASE_ORDER_ROOT } from "../pages/RouteConstants";

const { useBreakpoint } = Grid;

const PurchaseOrderUpdateStatusButtons = ({ purchaseOrderId }) => {
  const [prevStatus, setPrevStatus] = useState(null);
  const [nextStatus, setNextStatus] = useState(null);
  const navigate = useNavigate();

  const screenSize = useBreakpoint();

  const isSmallPhone = !screenSize["sm"];

  const { data: poStatuses } = useQuery({
    queryKey: ["trackingStatuses"],
    queryFn: async () => await PurchaseOrderService.getTrackingStatuses(),
    enabled: !!purchaseOrderId,
  });

  const { data: currentPo } = useQuery({
    queryKey: ["purchaseOrders", purchaseOrderId],
    queryFn: async () => await PurchaseOrderService.getPurchaseOrderById(purchaseOrderId),
    enabled: !!purchaseOrderId,
  });

  const { mutate: updateStatus } = useMutation({
    mutationFn: ({
      action,
      statusId,
    }) => PurchaseOrderService.updatePurchaseOrderStatus(purchaseOrderId, { action, statusId }),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["purchaseOrders", purchaseOrderId] });

      const currentIndex = poStatuses?.findIndex(pos => pos.enum === data?.statusID);

      setPrevStatus(poStatuses[currentIndex - 1] ?? null);
      setNextStatus(poStatuses[currentIndex + 1] ?? null);

      if (nextStatus?.enum === "ShipmentRequested") {
        nextStatus.name = "Request shipment";
      }

      message.success("Purchase order status updated successfully!");
    },
    onError: async (error) => {
      message.error(`Failed to update status. ${error.response?.data?.message}. Please try again.`);
    },
  });

  const handleNextStatus = (statusId) => {
    // shipment requested
    if (statusId === "ShipmentRequested")
      return navigate(`${PURCHASE_ORDER_ROOT}/new-shipment-request`, {
        state: {
          purchaseOrderId,
        },
      });

    updateStatus({ action: "next", statusId });
  };

  useEffect(() => {
    if (!poStatuses || !currentPo) return;

    const currentIndex = poStatuses?.findIndex(pos => pos.enum === currentPo?.statusID);

    setPrevStatus(poStatuses[currentIndex - 1] ?? null);
    setNextStatus(poStatuses[currentIndex + 1] ?? null);

  }, [poStatuses, currentPo]);

  if (nextStatus?.enum === "ShipmentRequested") {
    nextStatus.name = "Request shipment";
  }

  return (
    <>
      {currentPo?.statusID !== "Cancelled" &&
        <>
          <Flex justify={isSmallPhone ? "center" : "space-between"} align="center"
                wrap={isSmallPhone ? "wrap" : "nowrap"} gap={8}>
            {currentPo?.statusID !== "Submitted" &&
              <Popconfirm title={<>You are about to cancel this PO.<br />Please confirm if you wish to proceed.</>}
                          onConfirm={() => updateStatus({ action: "cancel", statusId: poStatuses[0]?.enum })}
              >
                <Flex gap={8} wrap={isSmallPhone ? "wrap" : "nowrap"} justify={isSmallPhone ? "center" : "start"}>
                  <Button className="button-secondary">
                    <FontAwesomeIcon icon={faXmark} /> Cancel PO
                  </Button>
                </Flex>
              </Popconfirm>
            }
            <Flex gap={8} wrap={isSmallPhone ? "wrap" : "nowrap"} justify={isSmallPhone ? "center" : "end"}>
              {currentPo?.statusID !== "Submitted" &&
                <Tooltip title={poStatuses?.find(pos => pos?.enum === prevStatus?.enum)?.name}>
                  <Button onClick={() => updateStatus({ action: "previous", statusId: prevStatus?.enum })}
                          className="button-secondary">
                    <FontAwesomeIcon icon={faAngleLeft} /> Previous Status
                  </Button>
                </Tooltip>
              }
              {nextStatus &&
                <Button onClick={() => handleNextStatus(nextStatus?.enum)}
                        className="button-primary"
                        type="primary">
                  {nextStatus?.name} <FontAwesomeIcon icon={faAngleRight} />
                </Button>
              }
            </Flex>
          </Flex>
        </>
      }
    </>
  );
};

export default PurchaseOrderUpdateStatusButtons;